import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Chip,
  List,
  ListItem,
  Alert,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  TrendingUp as IncomeIcon,
  TrendingDown as ExpenseIcon,
  Repeat as RepeatIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { BudgetEntry } from '../db/database';
import { useBudget } from '../context/BudgetContext';
import EditEntryForm from './EditEntryForm';

const EntryList: React.FC = () => {
  const { entries, deleteEntry, isLoading } = useBudget();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedEntry, setSelectedEntry] = useState<BudgetEntry | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, entry: BudgetEntry) => {
    setAnchorEl(event.currentTarget);
    setSelectedEntry(entry);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    setEditDialogOpen(true);
    handleMenuClose();
  };

  const handleDelete = async () => {
    if (selectedEntry?.id) {
      if (window.confirm('Are you sure you want to delete this entry?')) {
        await deleteEntry(selectedEntry.id);
      }
    }
    handleMenuClose();
  };

  const getCategoryColor = (category: string) => {
    const colors: { [key: string]: string } = {
      Income: 'success',
      'Credit Card': 'error',
      Loan: 'warning',
      Subscription: 'info',
      Vehicle: 'primary',
      Property: 'secondary',
      'Rent/Mortgage': 'warning',
      Other: 'default',
    };
    return colors[category] || 'default';
  };

  if (isLoading) {
    return (
      <Box p={2}>
        <Typography>Loading entries...</Typography>
      </Box>
    );
  }

  if (!entries || entries.length === 0) {
    return (
      <Box p={2}>
        <Alert severity="info">
          No entries found. Click the + button to add your first entry!
        </Alert>
      </Box>
    );
  }

  // Group entries by date
  const groupedEntries = entries.reduce((groups, entry) => {
    const dateKey = format(new Date(entry.date), 'yyyy-MM-dd');
    if (!groups[dateKey]) {
      groups[dateKey] = [];
    }
    groups[dateKey].push(entry);
    return groups;
  }, {} as { [key: string]: BudgetEntry[] });

  return (
    <>
      <List sx={{ p: 0 }}>
        {Object.entries(groupedEntries).map(([dateKey, dateEntries]) => (
          <Box key={dateKey} mb={2}>
            {/* Date Header */}
            <Box px={2} py={1} bgcolor="background.default">
              <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
                {format(new Date(dateKey), 'EEEE, MMMM d, yyyy')}
              </Typography>
            </Box>

            {/* Entries for this date */}
            {dateEntries.map((entry) => (
              <ListItem
                key={entry.id}
                sx={{
                  px: 2,
                  py: 1,
                  '&:hover': {
                    bgcolor: 'action.hover',
                  }
                }}
              >
                <Card sx={{ 
                  width: '100%', 
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderLeft: '4px solid',
                  borderLeftColor: entry.type === 'income' ? 'success.main' : 'error.main',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                    transform: 'translateY(-2px)',
                  }
                }}>
                  <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                    <Box display="flex" alignItems="flex-start" justifyContent="space-between">
                      <Box flex={1}>
                        <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
                          <Box sx={{
                            bgcolor: entry.type === 'income' ? 'success.light' : 'error.light',
                            borderRadius: '50%',
                            p: 0.75,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}>
                            {entry.type === 'income' ? (
                              <IncomeIcon sx={{ color: 'white', fontSize: 20 }} />
                            ) : (
                              <ExpenseIcon sx={{ color: 'white', fontSize: 20 }} />
                            )}
                          </Box>
                          <Typography variant="h6" component="div" fontWeight={600}>
                            {entry.title}
                          </Typography>
                        </Box>

                        <Box display="flex" alignItems="center" gap={1} mb={1.5} flexWrap="wrap">
                          <Chip
                            label={entry.category}
                            size="small"
                            color={getCategoryColor(entry.category) as any}
                            sx={{ fontWeight: 600 }}
                          />
                          {entry.isRecurring && (
                            <Chip
                              icon={<RepeatIcon />}
                              label={entry.recurringFrequency || 'Recurring'}
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ fontWeight: 600 }}
                            />
                          )}
                        </Box>

                        <Typography
                          variant="h5"
                          color={entry.type === 'income' ? 'success.main' : 'error.main'}
                          fontWeight={800}
                          sx={{ letterSpacing: '-0.5px' }}
                        >
                          {entry.type === 'income' ? '+' : '-'}£{entry.value.toFixed(2)}
                        </Typography>
                      </Box>

                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, entry)}
                        sx={{ 
                          mt: -0.5, 
                          mr: -0.5,
                          '&:hover': {
                            bgcolor: 'action.hover',
                          }
                        }}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              </ListItem>
            ))}
          </Box>
        ))}
      </List>

      {/* Context Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleEdit}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDelete}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Edit Dialog */}
      <EditEntryForm
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        entry={selectedEntry}
      />
    </>
  );
};

export default EntryList;
